// ============================================================================
// DISABLE REACT ERROR OVERLAY & SHOW SNACKBAR
// ============================================================================
// This file MUST be imported at the very top of index.tsx (before any other imports)

let initialized = false;

// Global snackbar function that will be set by the SnackbarProvider
let globalShowSnackbar: ((message: string, severity: 'error' | 'warning' | 'info' | 'success') => void) | null = null;

// Allow components to register the snackbar function
export const registerSnackbar = (showFn: typeof globalShowSnackbar) => {
  globalShowSnackbar = showFn;
};

// Show error in snackbar
const showErrorSnackbar = (message: string) => {
  if (globalShowSnackbar) {
    globalShowSnackbar(message, 'error');
  } else {
    // Fallback: create a simple snackbar if the provider isn't ready yet
    showFallbackSnackbar(message);
  }
};

// Fallback snackbar when MUI provider isn't available
const showFallbackSnackbar = (message: string) => {
  // Remove existing fallback snackbar if any
  const existing = document.getElementById('global-error-snackbar');
  if (existing) existing.remove();

  const snackbar = document.createElement('div');
  snackbar.id = 'global-error-snackbar';
  snackbar.innerHTML = `
    <div style="
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: #d32f2f;
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 999999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 12px;
      max-width: 500px;
      animation: slideIn 0.3s ease-out;
    ">
      <span style="font-size: 18px;">⚠️</span>
      <span style="flex: 1;">${message}</span>
      <button onclick="this.parentElement.parentElement.remove()" style="
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        font-size: 18px;
        padding: 0;
        line-height: 1;
      ">×</button>
    </div>
    <style>
      @keyframes slideIn {
        from { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
        to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
      }
    </style>
  `;
  document.body.appendChild(snackbar);

  // Auto-remove after 6 seconds
  setTimeout(() => snackbar.remove(), 6000);
};

// Remove CRA error overlay
const removeErrorOverlay = () => {
  // Target the iframe that CRA uses for error overlay
  const iframes = document.querySelectorAll('iframe');
  iframes.forEach((iframe) => {
    const style = iframe.getAttribute('style') || '';
    if (
      style.includes('position: fixed') ||
      style.includes('position:fixed')
    ) {
      iframe.remove();
    }
  });

  // Also target specific overlay IDs
  ['webpack-dev-server-client-overlay', 'webpack-dev-server-client-overlay-div'].forEach(id => {
    document.getElementById(id)?.remove();
  });

  // Remove any full-screen fixed divs that look like overlays
  document.querySelectorAll('body > div').forEach((div) => {
    const style = window.getComputedStyle(div);
    if (
      style.position === 'fixed' &&
      style.zIndex === '2147483647'
    ) {
      (div as HTMLElement).remove();
    }
  });
};

export const disableErrorOverlay = () => {
  if (initialized) return;
  if (typeof window === 'undefined') return;
  initialized = true;

  // Only run in development
  if (process.env.NODE_ENV !== 'development') return;

  // Add CSS to hide overlay immediately
  const style = document.createElement('style');
  style.id = 'hide-error-overlay-style';
  style.textContent = `
    /* Hide CRA error overlay */
    body > iframe[style*="position: fixed"],
    body > iframe[style*="position:fixed"],
    #webpack-dev-server-client-overlay,
    #webpack-dev-server-client-overlay-div {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;

  if (document.head) {
    document.head.appendChild(style);
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      document.head.appendChild(style);
    });
  }

  // MutationObserver to catch and remove overlay as soon as it appears
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) {
          if (node.tagName === 'IFRAME' || node.id?.includes('webpack') || node.id?.includes('overlay')) {
            node.style.display = 'none';
            node.remove();
          }
        }
      }
    }
  });

  const startObserver = () => {
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: false });
    }
  };

  if (document.body) {
    startObserver();
  } else {
    document.addEventListener('DOMContentLoaded', startObserver);
  }

  // Handle uncaught errors - show snackbar instead of overlay
  window.addEventListener('error', (event) => {
    event.preventDefault();

    // Remove overlay aggressively
    removeErrorOverlay();
    setTimeout(removeErrorOverlay, 10);
    setTimeout(removeErrorOverlay, 50);
    setTimeout(removeErrorOverlay, 100);

    // Show snackbar with error message
    const message = event.message || 'An unexpected error occurred';
    showErrorSnackbar(message);

    // Still log to console for debugging
    console.error('[GlobalError]', event.error || event.message);

    return true;
  }, true);

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    event.preventDefault();

    // Remove overlay
    removeErrorOverlay();
    setTimeout(removeErrorOverlay, 10);
    setTimeout(removeErrorOverlay, 50);
    setTimeout(removeErrorOverlay, 100);

    // Show snackbar
    const error = event.reason;
    const message = error instanceof Error ? error.message : String(error) || 'An unexpected error occurred';
    showErrorSnackbar(message);

    // Log to console
    console.error('[GlobalError] Unhandled Promise Rejection:', error);

    return true;
  }, true);

  console.info('[ErrorHandler] Global error handling with snackbar initialized');
};

// Auto-execute
disableErrorOverlay();
